import React, { useEffect, useState } from "react";
import PERMISSIONS, { apiFetch, getRundownTemplates } from "../utils/wkCommon";
import { getUser } from "../utils/auth";
import { hasUmatPermissionAsync } from "../utils/permissionsAsync";
import { isZh } from "../utils/language"


  /* FORMAT DATA */
  /*
    [
      {
        "items": [
          {
            "id": "r1",
            "daySeq": 1,
            "duration": 30,
            "penceramah": "Bpk. A",
            "penerjemah": "",
            "recordOwner": "admin",
            "recordEditor": null,
            "scheduleDesc": "Makna Pembukaan Kelas",
            "scheduleType": "Pembuka",
            "scheduleNotes": "Persiapan mic",
            "scheduleSubject": "開班意義",
            "showNotesOnReport": true
          },
          {
            "id": "r2",
            "daySeq": 1,
            "duration": 20,
            "penceramah": "Bpk. B",
            "penerjemah": "Sdri. C",
            "recordOwner": "admin",
            "recordEditor": null,
            "scheduleDesc": "",
            "scheduleType": "Topik/Sesi",
            "scheduleNotes": "",
            "scheduleSubject": "Dharma Talk",
            "showNotesOnReport": false
          }
        ],
        "daySeq": 1,
        "remarks": "Hari pertama - umum",
        "startTime": "08:00",
        "showEndTime": false
      }
    ]
  */

export default function AgendaBookRundownJson({
  agendaId,
	agendaBookId,
  agendaRundowns = [],
  onRundownUpdated
}) {
  const user = getUser();

  const [days, setDays] = useState(agendaRundowns || []);

  const [canReadAgendaRundown, setCanReadAgendaRundown] = useState(false);
  const [canWriteAgendaRundown, setCanWriteAgendaRundown] = useState(false);
  const [permsReady, setPermsReady] = useState(false);

	const [dragIndex, setDragIndex] = useState(null);
	const [dragOverIndex, setDragOverIndex] = useState(null);

	const [daySeq, setDaySeq] = useState(1);
	const [totalDays, setTotalDays] = useState(1);
  const currentDay = days.find(d => d.daySeq === daySeq);
  const timeline = buildTimeline(currentDay);

  const [loading, setLoading] = useState(false);

  const emptyRow = {
    id: null,
    duration: "",
    scheduleType: "",
    scheduleSubject: "",
    scheduleDesc: "",
    penceramah: "",
    penerjemah: "",
    scheduleNotes: "",
    showNotesOnReport: false
  };

  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState(emptyRow);
  const [isAdding, setIsAdding] = useState(false);

  const [isEditingDay, setIsEditingDay] = useState(false);

  const [dayDraft, setDayDraft] = useState({
    startTime: "",
    showEndTime: false,
    remarks: ""
  });

  const [rundownTemplates, setRundownTemplates] = useState([]);

  const [isInitLoading, setIsInitLoading] = useState(true);

  useEffect(() => {
    loadTemplates();
  }, []);

  async function loadTemplates() {

    const tpl = await getRundownTemplates();

    setRundownTemplates(tpl.rundown);
  }

  function applyTemplate(tpl){

    const updated = days.map(d => {

      if (d.daySeq !== daySeq) return d;

      const items = [...(d.items || [])];

      items.push({
        ...tpl,
        daySeq,
        recordOwner: user?.username,
        recordEditor: user?.username
      });

      return { ...d, items };

    });

    setDays(updated);
  }

  function handleAdd() {
    if (!canWriteAgendaRundown) return;
    setIsAdding(true);
    setEditingIndex(null);
    setDraft(emptyRow);
  }

  function handleEdit(idx) {
    if (!canWriteAgendaRundown) return;
    setIsAdding(false);
    setEditingIndex(idx);
    setDraft({ ...timeline[idx] });
  }

  function handleCancel() {
    setIsAdding(false);
    setEditingIndex(null);
    setDraft(emptyRow);
  }

  function handleDraftChange(field, value) {
    setDraft(prev => ({ ...prev, [field]: value }));
  }

  function handleSaveRow() {
    if (!canWriteAgendaRundown) return;

    if (!draft.duration || !draft.scheduleSubject) {
      alert(isZh() ? "請填寫必填欄位" : "Duration dan Subject wajib diisi.");
      return;
    }

    const updated = days.map(d => {
      if (d.daySeq !== daySeq) return d;

      const items = [...(d.items || [])];

      if (editingIndex === null) {
        items.push({
          id: `tmp-${Date.now()}-${Math.random()}`,
          ...draft,
          duration: Number(draft.duration),
          daySeq,
          recordOwner: user?.username,
          recordEditor: user?.username
        });
      } else {
        items[editingIndex] = {
          ...items[editingIndex],

          duration: Number(draft.duration),
          scheduleType: draft.scheduleType || "",
          scheduleSubject: draft.scheduleSubject || "",
          scheduleDesc: draft.scheduleDesc || "",

          // 🔥 IMPORTANT FIX
          penceramah: draft.penceramah ?? "",
          penerjemah: draft.penerjemah ?? "",

          scheduleNotes: draft.scheduleNotes || "",
          showNotesOnReport: !!draft.showNotesOnReport,

          recordEditor: user?.username
        };
      }

      setIsAdding(false);
      setEditingIndex(null);

      return { ...d, items };
    });

    setDays(updated);
    setDraft(emptyRow);
    setEditingIndex(null);
  }

	function handleDragStart(index) {
		setDragIndex(index);
	}

  function handleDragOver(e, index) {
    e.preventDefault();

    const rect = e.currentTarget.getBoundingClientRect();
    const middleY = rect.top + rect.height / 2;

    if (e.clientY < middleY) {
      setDragOverIndex(index);
    } else {
      setDragOverIndex(index + 1);
    }
  }

  function handleDrop(index) {
    if (dragIndex === null) return;

    setDays(prev =>
      prev.map(d => {
        if (d.daySeq !== daySeq) return d;

        const list = [...(d.items || [])];
        const draggedItem = list[dragIndex];

        // hapus dulu
        list.splice(dragIndex, 1);

        // 🔥 adjust index
        let insertIndex = index;
        if (dragIndex < index) {
          insertIndex = index - 1;
        }

        list.splice(insertIndex, 0, draggedItem);

        return { ...d, items: list };
      })
    );

    setDragIndex(null);
    setDragOverIndex(null);
  }

	function handleDragEnd() {
		setDragIndex(null);
		setDragOverIndex(null);
	}

	function handleDelete(index) {
		if (!canWriteAgendaRundown) return;

		const ok = window.confirm(
			isZh()
				? "確定刪除此流程？"
				: "Yakin ingin menghapus baris ini?"
		);

		if (!ok) return;

    const updated = days.map(d => {
      if (d.daySeq !== daySeq) return d;

      const items = [...d.items];
      items.splice(index, 1);

      return { ...d, items };
    });

    setDays(updated);
	}

  function PersonIcon({ variant }) {
    const color =
      variant === "translator"
        ? "#1e3a8a"   // biru lebih gelap
        : "#3b82f6";  // biru terang

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          marginRight: "4px",
          verticalAlign: "middle"
        }}
      >
        <path d="M20 21a8 8 0 0 0-16 0" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    );
  }

  function renderSpeaker(r) {
    if (!r.penceramah && !r.penerjemah) return null;

    return (
      <div className="r-speaker">
        {r.penceramah && (
          <span className="speaker-main">
            <PersonIcon variant="speaker" />
            {isZh() ? "授課 " : "Penceramah: "}
            {r.penceramah}
          </span>
        )}

        {r.penerjemah && (
          <span className="speaker-translator">
            {" "}
            (
            <PersonIcon variant="translator" />
            {isZh() ? "翻譯 " : "Terjemah: "}
            {r.penerjemah}
            )
          </span>
        )}
      </div>
    );
  }

  function addMinutes(time, mins) {
    if (!time) return "00:00";

    const [h, m] = time.split(":").map(Number);
    const total = h * 60 + m + (mins || 0);

    const nh = String(Math.floor(total / 60)).padStart(2, "0");
    const nm = String(total % 60).padStart(2, "0");

    return `${nh}:${nm}`;
  }

  function buildTimeline(day) {
    if (!day) return [];

    let current = day.startTime || "08:00";

    return day.items.map((item) => {
      const displayTime = current;
      current = addMinutes(current, item.duration || 0);

      return {
        ...item,
        displayTime
      };
    });
  }

  function handleCancel() {
    setIsAdding(false);
    setEditingIndex(null);
    setDraft(emptyRow);
  }

  async function handleSubmit() {
    if (!canWriteAgendaRundown) {
      alert(isZh() ? "無權限保存" : "Tidak ada hak untuk menyimpan.");
      return;
    }

    if (!agendaBookId) {
      alert("AgendaBook belum siap.");
      return;
    }

    try {
      setLoading(true);

      await apiFetch(`/api/agenda-books/${agendaBookId}`, {
        method: "PUT",
        body: {
          data: {
            agendaRundowns: days
          }
        }
      });

      // 🔥 RELOAD DATA TERBARU
      const fresh = await apiFetch(
        `/api/agenda-books/${agendaBookId}?populate=*`
      );

      const updatedRundowns =
        fresh?.data?.agendaRundowns || fresh?.agendaRundowns || [];

      setDays(updatedRundowns);

      // 🔥 sync ke parent
      if (onRundownUpdated) {
        onRundownUpdated(updatedRundowns);
      }

      alert(isZh() ? "流程表保存成功" : "Susunan Acara berhasil disimpan.");
    } catch (e) {
      console.error("Submit gagal:", e);
      alert(isZh() ? "流程表保存失敗" : "Gagal menyimpan.");
    } finally {
      setLoading(false);
    }
  }

	useEffect(() => {
		if (!agendaId) return;

		async function loadAgendaInfo() {
			try {
				const resp = await apiFetch(`/api/agendas/${agendaId}`);
				const a = resp?.data || resp;

				const start = a.agendaDate;
				const end = a.agendaDateEnd || a.agendaDate;

				if (start && end) {
					const d1 = new Date(start);
					const d2 = new Date(end);

					const diff =
						Math.floor((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;

					setTotalDays(diff > 0 ? diff : 1);

					if (daySeq > diff) setDaySeq(1);
				} else {
					setTotalDays(1);
				}
			} catch (e) {
				console.warn("loadAgendaInfo failed", e);
				setTotalDays(1);
			}
		}

		loadAgendaInfo();
	}, [agendaId]);

  /* ================= PERMISSION ================= */
  useEffect(() => {
    let mounted = true;

    async function loadPerms() {
      const email = user?.email || user?.LoginEmail;

      const r = await hasUmatPermissionAsync(
        email,
        PERMISSIONS.AGENDA_RUNDOWN_READ
      );

      const w = await hasUmatPermissionAsync(
        email,
        PERMISSIONS.AGENDA_RUNDOWN_WRITE
      );

      if (mounted) {
        setCanReadAgendaRundown(Boolean(r));
        setCanWriteAgendaRundown(Boolean(w));
        setPermsReady(true);
      }
    }

    loadPerms();
    return () => (mounted = false);
  }, [user]);

  useEffect(() => {
      setDays(agendaRundowns || []);
      setIsInitLoading(false);
  }, [agendaRundowns]);

  if (!permsReady) return <div>Memeriksa hak akses…</div>;

  if (!canReadAgendaRundown)
    return (
      <div style={{ color: "#888", fontStyle: "italic" }}>
        Anda tidak memiliki hak untuk melihat susunan acara.
      </div>
    );

  if (isInitLoading) {
    return (
      <div style={{
        padding: "1.5rem",
        textAlign: "center",
        color: "#64748b"
      }}>
        Loading rundown...
      </div>
    );
  }

if (isInitLoading) {
  return (
    <div>Loading rundown...</div>
  );
}

  // CHECK DAYS KOSONG
  if (!days || days.length === 0) {
    return (
      <div style={{ padding: "1.5rem", textAlign: "center" }}>
        <div style={{ marginBottom: "1rem", color: "#64748b" }}>
          {isZh() 
            ? "尚未建立流程表" 
            : "Rundown belum dibuat"}
        </div>

        {canWriteAgendaRundown && (
          <button
            className="btn btn-primary"
            onClick={handleCreateRundown}
          >
            {isZh() ? "建立流程表" : "Buat Rundown"}
          </button>
        )}
      </div>
    );
  }

  /* ================= CREATE RUNDOWN ================= */
  async function handleCreateRundown() {
    if (!agendaBookId) return;

    try {
      setLoading(true);

      const newDays = Array.from({ length: totalDays }).map((_, i) => ({
        daySeq: i + 1,
        startTime: "08:00",
        showEndTime: false,
        remarks: "",
        items: []
      }));

      // simpan ke backend
      await apiFetch(`/api/agenda-books/${agendaBookId}`, {
        method: "PUT",
        body: {
          data: {
            agendaRundowns: newDays
          }
        }
      });

      // reload
      const fresh = await apiFetch(
        `/api/agenda-books/${agendaBookId}?populate=*`
      );

      const updated =
        fresh?.data?.agendaRundowns || fresh?.agendaRundowns || [];

      setDays(updated);
      setDaySeq(1);

      alert(isZh() ? "流程表已建立" : "Rundown berhasil dibuat");

    } catch (e) {
      console.error(e);
      alert(isZh() ? "建立失敗" : "Gagal membuat rundown");
    } finally {
      setLoading(false);
    }
  }

  /* ================= EDIT HARI ================= */
  function handleEditDayInfo() {
    if (!canWriteAgendaRundown || !currentDay) return;

    setDayDraft({
      startTime: currentDay.startTime || "08:00",
      showEndTime: !!currentDay.showEndTime,
      remarks: currentDay.remarks || ""
    });

    setIsEditingDay(true);
  }

  function handleCancelDayEdit() {
    setIsEditingDay(false);
  }

  function handleSaveDayEdit() {
    const updated = days.map(d =>
      d.daySeq === daySeq
        ? {
            ...d,
            startTime: dayDraft.startTime,
            showEndTime: dayDraft.showEndTime,
            remarks: dayDraft.remarks
          }
        : d
    );

    setDays(updated);
    setIsEditingDay(false);
  }

  function getRowKey(r, idx) {
    return r.id ?? `tmp-${daySeq}-${idx}`;
  }

  return (
    <>
      <div className="rundown-list">

      <div style={{ marginBottom: "0.75rem" }}>

        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <div>
            {isZh() ? "第" : "Hari ke-"}
            <select
              value={daySeq}
              disabled={isEditingDay}
              onChange={(e) => { if (isEditingDay) return; setDaySeq(Number(e.target.value)); }}
              style={{ marginLeft: "0.5rem", width: "auto" }}
            >
              {Array.from({ length: totalDays }).map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
            {isZh() ? " 天" : ""}
          </div>

          {currentDay && (
          <>
            {!isEditingDay ? (
              <>
                <div style={{ paddingLeft: "1rem", color: "#64748b" }}>
                  {isZh() ? "開始時間: " : "Mulai: "}
                  <span style={{ fontWeight: "700", color: "Navy" }}>
                    {currentDay.startTime}
                  </span>
                </div>

                <div style={{ paddingLeft: "1rem", color: "#64748b" }}>
                  {isZh() ? "顯示結束時間: " : "Tampilkan waktu selesai: "}
                  <span style={{ fontWeight: "700", color: "Navy" }}>
                    {currentDay.showEndTime
                      ? (isZh() ? "✔ 是" : "✔ Ya")
                      : (isZh() ? "✕ 否" : "✕ Tidak")}
                  </span>
                </div>

                {canWriteAgendaRundown && (
                  <button
                    className="btn btn-secondary"
                    onClick={handleEditDayInfo}
                    style={{ marginLeft: "auto" }}
                  >
                    {isZh() ? "編輯當日資訊" : "Edit Hari"}
                  </button>
                )}
              </>
            ) : (
              <>
                <div style={{ paddingLeft: "1rem" }}>
                  <label style={{ fontSize: "0.85rem" }}>
                    {isZh() ? "開始時間" : "Waktu mulai"}
                  </label>
                  <input
                    type="time"
                    value={dayDraft.startTime}
                    onChange={(e) =>
                      setDayDraft(prev => ({ ...prev, startTime: e.target.value }))
                    }
                    style={{ width: "110px" }}
                  />
                </div>

                <div style={{ paddingLeft: "1rem" }}>
                  <label style={{ fontSize: "0.85rem" }}>
                    {isZh() ? "顯示結束時間" : "Tampilkan waktu selesai"}
                  </label>
                  <input
                    type="checkbox"
                    checked={dayDraft.showEndTime}
                    onChange={(e) =>
                      setDayDraft(prev => ({ ...prev, showEndTime: e.target.checked }))
                    }
                    style={{ width: "auto", marginLeft: "0.5rem" }}
                  />
                </div>

                <div style={{ marginLeft: "auto", display: "flex", gap: "0.5rem" }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancelDayEdit}
                  >
                    {isZh() ? "取消" : "Batal"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveDayEdit}
                  >
                    {isZh() ? "保存" : "Simpan"}
                  </button>
                </div>
              </>
            )}
          </>
        )}
        </div>
        {!isEditingDay ? (
          currentDay?.remarks && (
            <div
              style={{
                marginTop: "0.5rem",
                padding: "0.5rem 0.75rem",
                background: "#f8fafc",
                borderRadius: "0.5rem",
                color: "#475569",
                fontSize: "0.95rem",
                whiteSpace: "pre-wrap"
              }}
            >
              {currentDay.remarks}
            </div>
          )
        ) : (
          <div style={{ marginTop: "0.5rem" }}>
            <label style={{ fontSize: "0.85rem" }}>
              {isZh() ? "備註" : "Keterangan"}
            </label>
            <textarea
              value={dayDraft.remarks}
              onChange={(e) =>
                setDayDraft(prev => ({ ...prev, remarks: e.target.value }))
              }
              style={{ width: "100%", marginTop: "0.25rem" }}
            />
          </div>
        )}
      </div>

        <table className="r-table">
          <thead>
            <tr>
							<th className="r-th-drag"></th>
              <th className="r-th-time">{isZh() ? "時間" : "Waktu"}</th>
              <th>{isZh() ? "行程" : "Acara/Kegiatan"}</th>
              <th className="r-th-notes">{isZh() ? "備註" : "Catatan"}</th>
              {canWriteAgendaRundown && <th className="r-th-action"></th>}
            </tr>
          </thead>

          <tbody>
            {timeline.map((r, idx) => {
              const isLast = idx === timeline.length - 1;
              return (
                <tr
                  key={getRowKey(r, idx)}
                  className={
                    dragOverIndex === idx
                      ? "r-row r-row-drag-over"
                      : "r-row"
                  }
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={() => handleDrop(idx)}
                >
                  {/* DRAGGABLE COLUMN */}
                  <td className="r-td-drag">
                    {canWriteAgendaRundown && (
                      <span
                        className="drag-handle"
                        draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDrop={() => handleDrop(idx)}
                        onDragEnd={handleDragEnd}
                      >
                        ||
                      </span>
                    )}
                  </td>
                  {/* TIME */}
                  <td className="r-td-time">
                    <div style={{ fontWeight: 700 }}>
                      {r.displayTime}
                    </div>

                    {(!isLast || currentDay?.showEndTime) && (
                      <div style={{ fontSize: "0.9rem", color: "#64748b" }}>
                        ({r.duration} {isZh() ? "分鐘" : "menit"})
                      </div>
                    )}
                  </td>

                  {/* ACARA */}
                  <td>
                    <div>
                      {r.scheduleType && (
                        <span className="meta-pill">
                          {r.scheduleType}
                        </span>
                      )}
                      <span style={{ fontSize: "1.1rem", fontWeight: 700 }}>
                        {r.scheduleSubject}
                      </span>
                    </div>

                    {r.scheduleDesc && (
                      <div className="r-desc">
                        <span style={{ fontWeight: 700 }}>
                          {r.scheduleDesc}
                        </span>
                      </div>
                    )}

                    {(r.penceramah || r.penerjemah) && (
                      renderSpeaker(r)
                    )}
                  </td>

                  {/* NOTES */}
                  <td className="r-td-notes">
                    {r.scheduleNotes && (
                      <div
                        className={`r-notes-preview ${
                          r.scheduleNotes.length > 120 ? "clamp" : ""
                        }`}
                        title={r.scheduleNotes.length > 120 ? r.scheduleNotes : ""}
                      >
                        {r.scheduleNotes}
                      </div>
                    )}
                    {r.showNotesOnReport && (
                      <div className="r-print-flag">
                        <span className="r-print-icon">🖨️</span>
                        {isZh() ? " 列印於報表" : " Cetak di laporan"}
                      </div>
                    )}
                  </td>

                  {/* ACTION */}
                  {canWriteAgendaRundown && (
                    <td className="r-td-action">
                      <div className="r-action-group">

                        <button
                          className="btn btn-edit btn-icon"
                          title={isZh() ? "編輯 / Edit" : "Edit / 編輯"}
                          onClick={() => handleEdit(idx)}
                          disabled={!canWriteAgendaRundown}
                        >
                          ✏️
                        </button>

                        <button
                          className="btn btn-danger btn-icon"
                          title={isZh() ? "刪除 / Hapus" : "Hapus / 刪除"}
                          onClick={() => handleDelete(idx)}
                          disabled={!canWriteAgendaRundown}
                        >
                          ✕
                        </button>

                      </div>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>

        {canWriteAgendaRundown && !isAdding && editingIndex === null && (
          <div className="r-add-wrapper">
            <select
              className="r-template-select"
              value=""
              disabled={isEditingDay}
              onChange={(e) => {

                const val = e.target.value;

                if (val === "empty") {
                  handleAdd(); // tambah kosong
                  return;
                }

                const tpl = rundownTemplates[val];
                if (!tpl) return;

                applyTemplate(tpl);

              }}
            >

              <option value="">
                {isZh() ? "新增流程…" : "+ Tambah Rundown…"}
              </option>

              <option value="empty">
                {isZh() ? "新空白" : "Rundown Baru (Kosong)"}
              </option>

              {rundownTemplates.map((t, i) => (

                <option key={i} value={i}>
                  {t.scheduleSubject} / {t.scheduleDesc}
                </option>

              ))}

            </select>

            <button
              className="btn btn-ghost btn-full-center"
              style={{ display:"none" }}
              onClick={handleAdd}
            >
              {isZh() ? "+ 新增流程" : "+ Tambah Rundown"}
            </button>
          </div>
        )}

        {canWriteAgendaRundown && (editingIndex !== null || isAdding) && (
          <div className="rundown-card">

            <div className="r-inline">

              <div className="form-row r-col-duration">
                <label>{isZh() ? "時長 (分鐘) *" : "Durasi (menit) *"}</label>
                <input
                  type="number"
                  value={draft.duration}
                  onChange={(e) => handleDraftChange("duration", e.target.value)}
                />
              </div>

              <div className="form-row r-col-type">
                <label>{isZh() ? "類型" : "Kategori"}</label>
                <select
                  value={draft.scheduleType}
                  disabled={!canWriteAgendaRundown}
                  onChange={(e) => handleDraftChange("scheduleType", e.target.value)}
                >
                  <option value="">— pilih —</option>
                  <option value="Topik/Sesi">Topik / Sesi</option>
                  <option value="Shengge">Shengge</option>
                  <option value="Pembuka">Pembuka</option>
                  <option value="Penutup">Penutup</option>
                </select>
              </div>

            </div>

            <div className="form-row">
              <label>{isZh() ? "中文體目 / 主題 *" : "Acara/Kegiatan (Mandarin) *"}</label>
              <input
                value={draft.scheduleSubject}
                onChange={(e) => handleDraftChange("scheduleSubject", e.target.value)}
              />
            </div>

            <div className="form-row">
              <label>{isZh() ? "印尼文題目 / 描述" : "Acara/Kegiatan (Bahasa Indonesia)"}</label>
              <input
                value={draft.scheduleDesc}
                onChange={(e) => handleDraftChange("scheduleDesc", e.target.value)}
              />
            </div>

            <div className="r-inline">
              <div className="form-row">
                <label>{isZh() ? "授課" : "Penceramah"}</label>
                <input
                  value={draft.penceramah}
                  onChange={(e) => handleDraftChange("penceramah", e.target.value)}
                />
              </div>

              <div className="form-row">
                <label>{isZh() ? "翻譯" : "Penerjemah"}</label>
                <input
                  value={draft.penerjemah}
                  onChange={(e) => handleDraftChange("penerjemah", e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <label>{isZh() ? "備註" : "Catatan / Detil"}</label>
              <textarea
                value={draft.scheduleNotes}
                onChange={(e) => handleDraftChange("scheduleNotes", e.target.value)}
              />
            </div>

            <div className="r-notes-toggle">
              <input
                type="checkbox"
                checked={draft.showNotesOnReport}
                onChange={(e) =>
                  handleDraftChange("showNotesOnReport", e.target.checked)
                }
              />
              <label>{isZh() ? "顯示備註於報表" : "Tampilkan catatan di Report"}</label>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                {isZh() ? "取消" : "Batal"}
              </button>

              <button className="btn btn-primary" onClick={handleSaveRow}>
                {isZh() ? "保存" : "Simpan"}
              </button>
            </div>

          </div>
        )}

        {canWriteAgendaRundown && (
          <div
            style={{
              marginTop: "1rem",
              display: "flex",
              justifyContent: "flex-end"
            }}
          >
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading}
              style={{ opacity: loading ? 0.7 : 1 }}
            >
              {loading
                ? (isZh() ? "提交中..." : "Submitting...")
                : (isZh() ? "提交" : "Submit")}
            </button>
          </div>
        )}

        {/* DEBUG JSON */}
        <div style={{
          marginTop: "1.5rem",
          padding: "1rem",
          background: "#0f172a",
          color: "#e2e8f0",
          borderRadius: "0.75rem",
          fontSize: "0.85rem",
          fontFamily: "monospace",
          overflowX: "auto"
        }}>
          <div style={{ marginBottom: "0.5rem", fontWeight: "600", color: "#93c5fd" }}>
            JSON Preview (agendaRundowns)
          </div>

          <pre style={{ margin: 0 }}>
            {JSON.stringify(days, null, 2)}
          </pre>
        </div>        
      </div>

      <style>{`
        .r-table{width:100%;border-collapse:collapse;}
        .r-table th{text-align:left;padding:0.5rem;border-bottom:1px solid #e6eef8;font-size:1rem;}
        .r-table td{padding:0.6rem 0.5rem;vertical-align:top;border-bottom:1px solid #f1f5f9;font-size:1rem;}
        .r-th-time{width:6rem;}
        .r-th-notes{width:18rem;}
        .r-th-action{width:5rem;}
        .meta-pill{background:#e6eef8;color:#244f8f;padding:0.15rem 0.5rem;border-radius:999px;font-size:1rem;margin-right:0.4rem;}
        .r-desc{font-size:1rem;color:#64748b;margin-top:0.15rem;}
        .r-speaker{font-size:1rem;margin-top:0.25rem;}
        .notes-icon{cursor:pointer;}
        .r-td-action button{height:26px;width:26px;margin-right:0.2rem;padding:0;}
        .r-th-drag{width:28px;}
        .r-td-drag{width:28px;text-align:center;}
        .drag-handle{cursor:grab;font-weight:bold;color:#94a3b8;user-select:none;}
        .drag-handle:active{cursor:grabbing;}
        .r-row-drag-over{border-top:2px solid #3b82f6;}
        .speaker-main{color:#3b82f6;font-weight:600;}
        .speaker-translator{margin-left:1rem;color:#1e3a8a;font-weight:500;}
        .btn{padding:0.4rem 0.75rem;border-radius:0.5rem;border:none;cursor:pointer;}
        .btn-icon{display:flex;align-items:center;justify-content:center;}
        .btn-danger{background:#dc2626;color:#fff;border:none;}
        .btn-secondary{background:#475569;color:#fff;border:none;}
        .btn-primary{background:#2563eb;color:#fff;border:none;}
        .rundown-card{border:1px solid #e6eef8;border-radius:0.75rem;padding:1rem;margin-top:1rem;background:#fff;}
        .form-row{display:flex;flex-direction:column;margin-bottom:0.6rem;gap:0;}
        .form-row label{font-size:0.85rem;font-weight:600;margin-bottom:0.25rem;}
        input,select,textarea{font-size:0.9rem;padding:0.5rem;border-radius:0.5rem;border:1px solid #dbe3ef;width:98%;}
        .r-col-duration { width: 100px; padding-right: 0.3rem; }
        .r-col-type select { width: 200px; }
        .r-inline{display:flex;gap:1rem;}
        .r-col-duration{width:7rem;}
        .r-col-type{flex:1;}
        .r-notes-toggle{display:flex;align-items:left;gap:0.5rem;margin-bottom:0.75rem;font-size:0.85rem;font-weight:600;}
        .r-notes-toggle input{width:20px;}
        .r-action-group{display:flex;gap:0.4rem;align-items:center;}
        .btn-icon{width:30px;height:30px;padding:0;display:flex;align-items:center;justify-content:center;border-radius:0.5rem;transition:all 0.15s ease;}
        .btn-edit{background:#f1f5f9;color:#fff;opacity:0.85;}
        .btn-edit:hover{background:#eaecf0;}
        .btn-danger{background:#dc2626c6;color:#fff;opacity:0.9;}
        .btn-danger:hover{background:#dc2626f6;}
        .btn-ghost{background:#f1f5f9;color:#334155;border:1px solid #e2e8f0;}
        .btn-ghost:hover{background:#e2e8f0;}
        .btn-full-center{width:100%;text-align:center;justify-content:center;}
        .r-add-wrapper{margin-top:1rem;}
        .r-notes-preview{font-size:0.9rem;color:#475569;white-space:pre-wrap;}
        .r-notes-preview.clamp{display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis;cursor:pointer;}
        .r-print-flag{margin-top:0.25rem;font-size:0.8rem;color:#1e40af;display:flex;align-items:center;gap:0.25rem;}
        .r-print-icon{font-size:0.85rem;}
      `}</style>
    </>
  );
}